'use client';

import { useEffect, useRef } from 'react';
import type { Unsubscribable, v2 } from '@userlike/messenger';
import { logger } from '@packages/shared/src/utils/logger/logger';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { usePathname } from 'next/navigation';
import type { ChatBotConfig } from '@packages/config/src/default';
import { shouldDisplayChatBot } from './utils/shouldDisplayChatBot';

/**
 * Initializes and manages the Userlike Messenger (ChatBot) based on route changes.
 * This component handles the lifecycle of the ChatBot, ensuring it is correctly
 * mounted or unmounted as the application's route changes. It leverages the provided
 * configuration to control the visibility and behavior of the ChatBot across different
 * parts of the application.
 *
 * @param {ChatBotConfig} props The configuration for the ChatBot, including which paths
 * it should appear on and its visibility settings.
 */
const ChatBotInit = ({
  visibility,
  widgetKey,
}: Pick<ChatBotConfig, 'visibility'> & { widgetKey: string }) => {
  let subscription: Unsubscribable;
  let messenger: v2.Messenger;
  const mounted = useRef(false);
  const subscriptionRef = useRef<Unsubscribable>(undefined);

  /**
   * Asynchronously creates and mounts the ChatBot API.
   */
  const createAndMountApi = async () => {
    const { createMessenger } = await import('@userlike/messenger');

    const result = await createMessenger({
      version: 2,
      widgetKey,
    });

    if (result.kind === 'error') {
      logger.error(`Chatbot: Failed to create Messenger: ${result.error}`);
      return;
    }

    const { api } = result.value;
    if (api === null) {
      logger.error('Chatbot: API reached end-of-life, please check documentation and upgrade.');
      return;
    }

    subscription = api.mount().subscribe(async (subscribeResult) => {
      if (subscribeResult.kind === 'error' || subscribeResult.value === null) {
        return;
      }

      messenger = subscribeResult.value;
      await messenger.setVisibility(visibility);
    });
    subscriptionRef.current = subscription;
  };

  /**
   * Manages ChatBot mounting/unmounting via useEffect.
   * @see createAndMountApi
   */
  useEffect(() => {
    const mountChatBot = async () => {
      if (!subscriptionRef.current) {
        try {
          await createAndMountApi();
        } catch (e) {
          logger.error(e, `Failed to mount ChatBot messenger: ${e}`);
        }
      }
    };

    const unmountChatBot = async () => {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current?.unsubscribe();
          subscriptionRef.current = undefined;
        } catch (e) {
          logger.error(e, `Failed to unmount ChatBot messenger: ${e}`);
        }
      }
    };

    // just to avoid errors in strict mode. does not affect react production mode
    if (!mounted.current) {
      mountChatBot();
      mounted.current = true;
    }

    /**
     * Cleanup function to unmount the ChatBot when the component is unmounted.
     */
    return () => {
      unmountChatBot();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

/**
 * A wrapper component for `ChatBotInit`. This component is solely focused on side effects,
 * specifically for mounting or unmounting the chatbot, and does not render any visual UI elements.
 * As such, it consistently returns `null` after performing its operations.
 *
 * @see https://github.com/userlike/messenger
 * @see https://docs.userlike.com/setup/integration/api
 * @returns {null}
 */
export const ChatBot = () => {
  const { chatBot: chatBotConfig } = useConfig();
  const pathname = usePathname();
  const { key, includePaths, excludePaths } = chatBotConfig;
  const showChatBot = shouldDisplayChatBot({ path: pathname || '', includePaths, excludePaths });

  if (chatBotConfig.enabled && key !== '' && showChatBot) {
    return <ChatBotInit visibility={chatBotConfig.visibility} widgetKey={key} />;
  }

  return null;
};
